import React from 'react';

const Step3SchedulingMethod = React.memo(function Step3SchedulingMethod({
  userType,
  newPatientData,
  existingPatientResult,
  assignedProvider,
  assignedLocation,
  setSchedulingMethod,
  addToDebugLog,
  handleNextStep,
}) {
  return (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Choose Your Scheduling Method</h2>
        <p className="text-gray-600">Select how you'd like to find your appointment</p>
      </div>

      <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mb-6">
        <div className="flex items-center text-orange-900">
          <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
          </svg>
          <span className="font-semibold">
            {userType === 'new'
              ? `${newPatientData.first_name} ${newPatientData.last_name}`
              : `${existingPatientResult?.FirstName} ${existingPatientResult?.LastName}`}
          </span>
        </div>
      </div>

      {/* Existing patient: assigned provider is unknown/empty — block scheduling and direct to call */}
      {userType === 'existing' && assignedProvider && !assignedProvider.name && (
        <div className="bg-red-50 border-2 border-red-300 rounded-xl p-5 mb-6">
          <div className="flex items-start">
            <svg className="w-6 h-6 text-red-600 mr-3 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <div>
              <h4 className="font-semibold text-red-900 mb-1">Provider Information Unavailable</h4>
              <p className="text-sm text-red-800 mb-3">
                We were unable to find this patient's assigned provider's current schedule. This may mean their provider has changed locations, is no longer with NAMHS, or their patient record needs to be updated.
              </p>
              <p className="text-sm text-red-800">
                Please call <strong>(530) 806-0805</strong> to confirm the provider assignment before scheduling.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Existing patient: known assigned provider — show prominently before method selection */}
      {userType === 'existing' && assignedProvider && assignedProvider.name && assignedLocation && (
        <div className="bg-green-50 border-2 border-green-300 rounded-xl p-5 mb-6">
          <div className="flex items-start">
            <svg className="w-6 h-6 text-green-600 mr-3 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div className="flex-1">
              <h4 className="font-semibold text-green-900 mb-2">Assigned Provider Found</h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm bg-white rounded-lg p-3 border border-green-200">
                <div>
                  <span className="text-gray-500 text-xs uppercase tracking-wide block mb-0.5">Provider</span>
                  <span className="font-semibold text-gray-900">{assignedProvider.name}</span>
                </div>
                <div>
                  <span className="text-gray-500 text-xs uppercase tracking-wide block mb-0.5">Location</span>
                  <span className="font-semibold text-gray-900">{assignedLocation.name}</span>
                </div>
              </div>
              <p className="text-xs text-green-700 mt-2">
                Scheduling will be limited to this provider and location to ensure continuity of care. To request a provider change, please call <strong>(530) 806-0805</strong>.
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Disable scheduling method buttons when provider info is unknown */}
        <button
          onClick={() => {
            setSchedulingMethod('find-next-available');
            addToDebugLog('Scheduling Method Selected', { method: 'find-next-available' });
            handleNextStep();
          }}
          disabled={userType === 'existing' && assignedProvider && !assignedProvider.name}
          className="group relative bg-gradient-to-br from-orange-50 to-amber-50 border-2 border-orange-200 rounded-xl p-8 hover:border-orange-400 hover:shadow-xl transition-all text-left disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:border-orange-200 disabled:hover:shadow-none"
        >
          <div className="absolute top-4 right-4">
            <svg className="w-8 h-8 text-orange-500 group-hover:scale-110 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
            </svg>
          </div>

          <div className="mb-4">
            <h3 className="text-xl font-bold text-gray-900 mb-2">Find Next Available Appointment</h3>
            <div className="w-12 h-1 bg-orange-500 mb-3"></div>
            <p className="text-gray-700 text-sm mb-4">
              Let us find the next available appointment for you across all locations and providers.
            </p>
          </div>

          <div className="space-y-2 text-sm">
            {['Urgent Care', 'Appointments available within the next 10 business days', 'Faster, automated search results'].map((item) => (
              <div key={item} className="flex items-start">
                <svg className="w-4 h-4 text-green-600 mr-2 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <span className="text-gray-700">{item}</span>
              </div>
            ))}
          </div>

          <div className="mt-6 flex items-center text-orange-600 font-semibold group-hover:translate-x-2 transition-transform">
            <span>Select This Option</span>
            <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
            </svg>
          </div>
        </button>

        <button
          onClick={() => {
            setSchedulingMethod('manual-selection');
            addToDebugLog('Scheduling Method Selected', { method: 'manual-selection' });
            handleNextStep();
          }}
          disabled={userType === 'existing' && assignedProvider && !assignedProvider.name}
          className="group relative bg-gradient-to-br from-blue-50 to-indigo-50 border-2 border-blue-200 rounded-xl p-8 hover:border-blue-400 hover:shadow-xl transition-all text-left disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:border-blue-200 disabled:hover:shadow-none"
        >
          <div className="absolute top-4 right-4">
            <svg className="w-8 h-8 text-blue-500 group-hover:scale-110 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
          </div>

          <div className="mb-4">
            <h3 className="text-xl font-bold text-gray-900 mb-2">Choose Your Own Appointment</h3>
            <div className="w-12 h-1 bg-blue-500 mb-3"></div>
            <p className="text-gray-700 text-sm mb-4">
              Choose your preferred date, location, and provider yourself.
            </p>
          </div>

          <div className="space-y-2 text-sm">
            {['Choose your location', 'Pick specific date', 'Select your provider'].map((item) => (
              <div key={item} className="flex items-start">
                <svg className="w-4 h-4 text-green-600 mr-2 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <span className="text-gray-700">{item}</span>
              </div>
            ))}
          </div>

          <div className="mt-6 flex items-center text-blue-600 font-semibold group-hover:translate-x-2 transition-transform">
            <span>Select This Option</span>
            <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
            </svg>
          </div>
        </button>
      </div>
    </div>
  );
});

export default Step3SchedulingMethod;
